import { Box, CircularProgress } from '@mui/material';
import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';

import SystemUnavailable from '@/components/SystemUnavailable';
import PageNotFound from '@/pages/PageNotFound';

// Set when we reload to recover from a stale chunk. A deploy replaces every
// hashed asset, so a tab still running the previous index.html asks for chunk
// URLs that no longer exist; one reload picks up the current index.html and its
// new hashes. The timestamp bounds that: if the very next load fails the same
// way, the build itself is broken and reloading again would just spin.
const RELOAD_KEY = 'overwatch:last-chunk-reload';
const RELOAD_COOLDOWN_MS = 60_000;

// Wording differs per browser: Chrome "Failed to fetch dynamically imported
// module", Firefox "error loading dynamically imported module", Safari
// "Importing a module script failed". Vite also emits its own CSS preload
// failure through the same path.
const CHUNK_ERROR_PATTERN =
  /dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError/i;

const isChunkLoadError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return CHUNK_ERROR_PATTERN.test(message);
};

const recentlyReloaded = () => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    return Date.now() - last < RELOAD_COOLDOWN_MS;
  } catch {
    // Private-mode Safari throws on sessionStorage; treat as "already tried"
    // rather than risk an unbounded reload loop.
    return true;
  }
};

const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError();
  const staleChunk = isChunkLoadError(error);
  const willReload = staleChunk && !recentlyReloaded();

  useEffect(() => {
    if (!willReload) return;
    try {
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    } catch {
      return;
    }
    window.location.reload();
  }, [willReload]);

  if (isRouteErrorResponse(error) && error.status === 404) return <PageNotFound />;

  if (willReload) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  console.error('Unhandled route error', error);

  return <SystemUnavailable onRetry={() => window.location.reload()} />;
};

export default RouteErrorBoundary;
